import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold, Part } from "@google/generative-ai";
import { Constants } from "../utils/constants";
import logger from "../utils/logger";

async function fileToGenerativePart(file: File): Promise<Part> {
  const base64EncodedDataPromise = new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64 = reader.result.split(",", 2)[1];
        resolve(base64);
      } else {
        reject(new Error("Failed to convert file to base64 string"));
      }
    };
    reader.onerror = err => reject(err);
  });

  return {
    inlineData: {
      data: await base64EncodedDataPromise,
      mimeType: file.type,
    },
  };
}

class GeminiService {
  private readonly apiKey: string;
  private genAI: GoogleGenerativeAI;
  private readonly modelName: string = "gemini-1.5-flash";

  constructor() {
    this.apiKey = Constants.firebaseConfig.apiKey;

    if (!this.apiKey) {
      logger.error("Firebase API Key (used for Gemini) is missing from Constants!");
      throw new Error("Firebase API Key (used for Gemini) is not configured. Service cannot be initialized.");
    }
    logger.info("Initializing Gemini with Firebase API Key...");
    this.genAI = new GoogleGenerativeAI(this.apiKey);
    logger.info("GoogleGenerativeAI client initialized successfully.");
  }

  private safetySettings = [
    {
      category: HarmCategory.HARM_CATEGORY_HARASSMENT,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
    },
    {
      category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
    },
    {
      category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
    },
    {
      category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
    },
  ];

  private async generateContent(promptParts: (string | Part)[]): Promise<string> {
    try {
      const model = this.genAI.getGenerativeModel({
        model: this.modelName,
        safetySettings: this.safetySettings,
      });

      const result = await model.generateContent(promptParts);
      const response = result.response;

      if (!response) {
        throw new Error("No response received from Gemini API.");
      }

      if (response.promptFeedback?.blockReason) {
        logger.warn(`Gemini request blocked due to: ${response.promptFeedback.blockReason}`);
        throw new Error(`Request blocked by safety settings: ${response.promptFeedback.blockReason}`);
      }

      return response.text();
    } catch (error: any) {
      logger.error("Error generating content with Gemini:", error);
      throw new Error(`Gemini API request failed: ${error.message || error}`);
    }
  }

  public async extractEmailsFromText(text: string): Promise<string[]> {
    try {
      const prompt = `
                Extract all valid email addresses from the following text.
                Return ONLY a valid JSON array of strings containing the email addresses.
                For example: ["email1@example.com", "email2@example.com"]

                Text to process:
                ${text}
            `;

      const responseText = await this.generateContent([prompt]);
      return this.parseEmailsFromResponse(responseText);
    } catch (error) {
      logger.error("Error extracting emails from text:", error);
      throw new Error("Failed to extract emails from text");
    }
  }

  public async extractEmailsFromFile(file: File): Promise<string[]> {
    try {
      if (!file) {
        throw new Error("No file provided");
      }
      logger.info(`[extractEmailsFromFile] Starting extraction for: ${file.name}, Type: ${file.type}`);

      let promptParts: (string | Part)[] = [];
      const basePrompt = `
                Extract all valid email addresses from this file content.
                Return ONLY a valid JSON array of strings containing the email addresses.
                Make sure to find all email addresses in the format user@domain.com.
                For example: ["email1@example.com", "email2@example.com"]
            `;

      if (file.type.startsWith("text/") || file.name.endsWith(".txt") || file.name.endsWith(".csv")) {
        logger.info(`[extractEmailsFromFile] Reading file as text: ${file.name}`);
        const text = await this.readFileAsText(file);
        logger.debug(`[extractEmailsFromFile] Text content read:`, text);
        const fullPrompt = `${basePrompt}\n\nFile Text Content:\n${text}`;
        logger.debug("[extractEmailsFromFile] Prompt being sent for text file:", fullPrompt);
        promptParts = [fullPrompt];
      } else if (file.type.startsWith("image/") || file.type === "application/pdf" || file.name.endsWith(".pdf")) {
        logger.info(`[extractEmailsFromFile] Reading file as image/document: ${file.name}`);
        const imagePart = await fileToGenerativePart(file);
        logger.debug("[extractEmailsFromFile] Prompt being sent for image/doc file:", basePrompt);
        promptParts = [basePrompt, imagePart];
      } else if (
        file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/msword" ||
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".doc") ||
        file.name.endsWith(".docx")
      ) {
        logger.warn(`[extractEmailsFromFile] Treating document type ${file.type} as image for extraction.`);
        try {
          const docAsImagePart = await fileToGenerativePart(file);
          logger.debug("[extractEmailsFromFile] Prompt being sent for doc-as-image file:", basePrompt);
          promptParts = [basePrompt, docAsImagePart];
        } catch (conversionError: any) {
          logger.error(
            `[extractEmailsFromFile] Failed to convert document ${file.name} to image part: ${conversionError.message}`
          );
          throw new Error(`Could not process document file: ${file.name}`);
        }
      } else {
        logger.warn(`[extractEmailsFromFile] Attempting text extraction for unknown file type: ${file.type}`);
        try {
          const text = await this.readFileAsText(file);
          logger.debug(`[extractEmailsFromFile] Text content read (fallback):`, text);
          const fullPrompt = `${basePrompt}\n\nFile Text Content:\n${text}`;
          logger.debug("[extractEmailsFromFile] Prompt being sent for fallback text file:", fullPrompt);
          promptParts = [fullPrompt];
        } catch (readError) {
          logger.warn(
            `[extractEmailsFromFile] Could not read file ${file.name} as text, attempting image processing as last resort.`
          );
          try {
            const imagePart = await fileToGenerativePart(file);
            logger.debug("[extractEmailsFromFile] Prompt being sent for fallback image file:", basePrompt);
            promptParts = [basePrompt, imagePart];
          } catch (imageError: any) {
            logger.error(
              `[extractEmailsFromFile] Failed to process file ${file.name} as text or image: ${imageError.message}`
            );
            throw new Error(`Unsupported file type or error reading file: ${file.type}`);
          }
        }
      }

      if (promptParts.length === 0) {
        logger.error("[extractEmailsFromFile] Could not prepare content for Gemini processing.");
        throw new Error("Could not prepare content for Gemini processing.");
      }

      logger.info("[extractEmailsFromFile] Sending request to Gemini generateContent...");
      const responseText = await this.generateContent(promptParts);
      logger.debug("[extractEmailsFromFile] Raw response text from Gemini:", responseText);

      logger.info("[extractEmailsFromFile] Parsing Gemini response...");
      return this.parseEmailsFromResponse(responseText);
    } catch (error: any) {
      logger.error("[extractEmailsFromFile] Error during extraction:", error);

      throw new Error(`Failed to extract emails from file '${file?.name || "unknown"}': ${error.message}`);
    }
  }

  public async preprocessFile(file: File): Promise<string[]> {
    return this.extractEmailsFromFile(file);
  }

  private parseEmailsFromResponse(response: string): string[] {
    try {
      const cleanedResponse = response.replace(/```json\n?|```/g, "").trim();
      logger.debug("[parseEmailsFromResponse] Cleaned response:", cleanedResponse);

      let jsonArray;
      try {
        logger.debug("[parseEmailsFromResponse] Attempting direct JSON parse...");
        jsonArray = JSON.parse(cleanedResponse);
        logger.debug("[parseEmailsFromResponse] Direct parse successful:", jsonArray);
      } catch (parseError) {
        logger.warn("[parseEmailsFromResponse] Direct JSON parsing failed, attempting regex extraction.", parseError);
        const match = cleanedResponse.match(/(\[.*?\])/s);
        logger.debug("[parseEmailsFromResponse] Regex match result:", match);
        if (match && match[1]) {
          try {
            logger.debug("[parseEmailsFromResponse] Attempting JSON parse on regex match...");
            jsonArray = JSON.parse(match[1]);
            logger.debug("[parseEmailsFromResponse] Regex parse successful:", jsonArray);
          } catch (regexParseError) {
            logger.error("[parseEmailsFromResponse] Failed to parse extracted JSON array string:", regexParseError);
            logger.error("[parseEmailsFromResponse] Original Gemini Response Text:", response);
            return [];
          }
        } else {
          logger.error("[parseEmailsFromResponse] Could not find JSON array structure in response via regex.");
          logger.error("[parseEmailsFromResponse] Original Gemini Response Text:", response);
          return [];
        }
      }

      if (!Array.isArray(jsonArray)) {
        logger.error("[parseEmailsFromResponse] Parsed structure is not an array:", jsonArray);
        logger.error("[parseEmailsFromResponse] Original Gemini Response Text:", response);
        return [];
      }

      const emails = jsonArray
        .filter(item => {
          const isString = typeof item === "string";

          const isValidEmail = isString && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.trim());
          if (isString && !isValidEmail) {
            logger.warn(`[parseEmailsFromResponse] Filtering out invalid email format:`, item);
          }
          return isValidEmail;
        })
        .map(email => email.trim());

      logger.info(`[parseEmailsFromResponse] Successfully parsed ${emails.length} emails.`);
      return emails;
    } catch (error: any) {
      logger.error("[parseEmailsFromResponse] Critical error during parsing:", error);
      logger.error("[parseEmailsFromResponse] Original Gemini Response Text:", response);
      return [];
    }
  }

  private async readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file as text"));
        }
      };
      reader.onerror = error => reject(error);
    });
  }
}

export const geminiService = new GeminiService();
export default geminiService;
